import { CloseOutlined, CreditCardOutlined, NumberOutlined } from '@ant-design/icons';
import { theme } from 'antd';
import { CreditCardIcon } from 'assets/images/svg';
import { CREDENTIAL_TYPE_PASSCODE } from 'config/credentialTypes';
import { groupCredentialsByPhysicalIdentifier } from 'library/helpers/accessControl';
import { useManagePermittedRoles } from 'library/hooks';
import React, { useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { selectCredentials, selectCredentialsByPersonID, selectLoadingCredentialIDs } from 'reduxStore/credentials/selectors';
import { credentialsActions } from 'reduxStore/credentials/slice';
import { Button, Col, Divider, Empty, Flex, Modal, Row, Select, Space, Spin, Tag, Tooltip, Typography } from 'zui/Antd';

const { Title, Text } = Typography;

const ManageCredentialsModal = ({ children, visible: visibleProp, setVisible: setVisibleProp, personID }) => {
	const dispatch = useDispatch();
	const {
		paddingLG,
		paddingXS,
		paddingSM,
		fontSizeSM,
		colorTextDescription,
		paddingXL,
		fontWeightStrong,
		colorIcon,
		paddingXXS
	} = theme.useToken().token;

	// Permission checks
	const canManageCredential = useManagePermittedRoles('updateCredential');
	const permissionDeniedMessage = "You don't have permission to perform this action.";

	const allCredentials = useSelector(selectCredentials);
	const personCredentials = useSelector((state) => selectCredentialsByPersonID(state, personID));
	const loadingCredentialIDs = useSelector(selectLoadingCredentialIDs);
	const loadingUpdateCredential = Object.keys(loadingCredentialIDs).length > 0;

	const groupedPersonCredentials = useMemo(() => {
		return groupCredentialsByPhysicalIdentifier(personCredentials);
	}, [personCredentials]);

	const [newAssociatedCredentials, setNewAssociatedCredentials] = useState([]);
	const [removedAssociatedCredentials, setRemovedAssociatedCredentials] = useState([]);
	const [hasChanges, setHasChanges] = useState(false);
	const [internalVisible, setInternalVisible] = useState(false);

	// Support both external control (visibleProp/setVisibleProp) and internal state
	const visible = visibleProp !== undefined ? visibleProp : internalVisible;
	const setVisible = setVisibleProp !== undefined ? setVisibleProp : setInternalVisible;

	const availableCredentials = useMemo(() => {
		const existing = groupedPersonCredentials.map((c) => c.credentialID) || [];
		const filtered = existing.filter((id) => !removedAssociatedCredentials.includes(id));
		const excluded = new Set([...filtered, ...newAssociatedCredentials]);

		// Only show unassigned credentials (no personID)
		const grouped = groupCredentialsByPhysicalIdentifier(
			allCredentials.filter((c) => !c.personID && !excluded.has(c.credentialID))
		);

		return grouped
			.sort((a, b) => {
				const aIsPasscode = a.credentialTypeID === CREDENTIAL_TYPE_PASSCODE;
				const bIsPasscode = b.credentialTypeID === CREDENTIAL_TYPE_PASSCODE;
				if (aIsPasscode !== bIsPasscode) return aIsPasscode ? 1 : -1;
				return (a.value || a.name || '').localeCompare(b.value || b.name || '', undefined, { numeric: true });
			})
			.map((c) => {
				const isPasscode = c.credentialTypeID === CREDENTIAL_TYPE_PASSCODE;
				const searchText = `${c.name || ''} ${c.value || ''}`.toLowerCase();
				return {
					label: (
						<Flex align='center' gap={8} style={{ width: '100%' }}>
							{isPasscode ? <NumberOutlined /> : <CreditCardIcon />}
							<Text style={{ flex: 1 }} ellipsis>
								{c.name} - {c.value}
							</Text>
						</Flex>
					),
					value: c.credentialID,
					searchText
				};
			});
	}, [allCredentials, groupedPersonCredentials, newAssociatedCredentials, removedAssociatedCredentials]);

	// ✅ Handle cancel and reset changes
	const handleCancel = () => {
		setNewAssociatedCredentials([]);
		setRemovedAssociatedCredentials([]);
		setHasChanges(false);
		setVisible(false);
	};

	// ✅ Add credential
	const handleAddCredential = (credentialIDToAdd) => {
		if (removedAssociatedCredentials.includes(credentialIDToAdd)) {
			const updatedRemovedCredentials = removedAssociatedCredentials.filter((id) => id !== credentialIDToAdd);
			setRemovedAssociatedCredentials(updatedRemovedCredentials);
			setHasChanges(newAssociatedCredentials.length > 0 || updatedRemovedCredentials.length > 0);
		} else {
			const updatedNewCredentials = [...newAssociatedCredentials, credentialIDToAdd];
			setNewAssociatedCredentials(updatedNewCredentials);
			setHasChanges(updatedNewCredentials.length > 0 || removedAssociatedCredentials.length > 0);
		}
	};

	// ✅ Remove credential
	const handleRemoveCredential = (credentialIDToRemove) => {
		if (newAssociatedCredentials.includes(credentialIDToRemove)) {
			const updatedNewCredentials = newAssociatedCredentials.filter((id) => id !== credentialIDToRemove);
			setNewAssociatedCredentials(updatedNewCredentials);
			setHasChanges(updatedNewCredentials.length > 0 || removedAssociatedCredentials.length > 0);
		} else {
			const updatedRemovedCredentials = [...removedAssociatedCredentials, credentialIDToRemove];
			setRemovedAssociatedCredentials(updatedRemovedCredentials);
			setHasChanges(newAssociatedCredentials.length > 0 || updatedRemovedCredentials.length > 0);
		}
	};

	// ✅ Submit changes
	const handleSubmit = () => {
		const credentialsToAdd = newAssociatedCredentials.filter((id) => !removedAssociatedCredentials.includes(id));
		const credentialsToRemove = removedAssociatedCredentials.filter((id) => !newAssociatedCredentials.includes(id));

		// Assign credentials to person
		if (credentialsToAdd.length > 0) {
			credentialsToAdd.forEach((credentialIDToAdd) => {
				dispatch(
					credentialsActions.assignCredential({
						credentialID: credentialIDToAdd,
						data: { newPersonID: personID }
					})
				);
			});
		}

		// Unassign credentials from person
		if (credentialsToRemove.length > 0) {
			credentialsToRemove.forEach((credentialIDToRemove) => {
				dispatch(
					credentialsActions.unassignCredential({
						credentialID: credentialIDToRemove,
						data: { oldPersonID: personID }
					})
				);
			});
		}

		handleCancel();
	};

	return (
		<>
			{children ? (
				<span onClick={() => canManageCredential && setVisible(true)}>{children}</span>
			) : (
				<Tooltip
					title={!canManageCredential ? permissionDeniedMessage : "Manage this Person's Credentials"}
					placement='top'
				>
					<span>
						<Button
							type='text'
							size='small'
							icon={<CreditCardIcon color='currentColor' />}
							disabled={!canManageCredential}
							onClick={(e) => {
								e.stopPropagation();
								setVisible(true);
							}}
						/>
					</span>
				</Tooltip>
			)}

			<Modal
				open={visible}
				onCancel={handleCancel}
				closable
				centered
				destroyOnClose
				width={763}
				footer={[
					<>
						<Divider />

						<Flex align='center' justify='flex-end' gap={paddingSM} key='footer'>
							<Button onClick={handleCancel} type='default' disabled={loadingUpdateCredential}>
								Cancel
							</Button>
							<Button
								onClick={handleSubmit}
								type='primary'
								loading={loadingUpdateCredential}
								disabled={!hasChanges}
							>
								Save Changes
							</Button>
						</Flex>
					</>
				]}
				styles={{ content: { padding: `${paddingLG}px ${paddingXL}px` } }}
			>
				<Row gutter={[paddingLG, paddingLG]} style={{ paddingBottom: paddingLG }} align='middle'>
					<Col span={24}>
						<Title level={4} style={{ margin: 0 }}>
							Manage Credentials
						</Title>
						<Text style={{ lineHeight: '20px', fontSize: fontSizeSM, color: colorTextDescription }}>
							Manage this person's credentials by adding or removing credentials from the list.{' '}
						</Text>
					</Col>

					<Col span={24}>
						<Flex vertical gap={paddingXS}>
							<Text strong>Assign Credential</Text>
							<Select
								showSearch
								placeholder='Search by name or number...'
								style={{ width: '100%' }}
								options={availableCredentials}
								value={null}
								onSelect={handleAddCredential}
								filterOption={(input, option) =>
									(option?.searchText || '').includes(input.toLowerCase())
								}
							/>
						</Flex>
					</Col>

					<Col span={24}>
						<Flex vertical gap={paddingSM}>
							<Text strong>Existing Credentials</Text>
							{groupedPersonCredentials.filter(
								(c) => !removedAssociatedCredentials.includes(c.credentialID)
							).length === 0 && newAssociatedCredentials.length === 0 ? (
								<Flex
									justify='center'
									align='center'
									style={{
										width: '100%'
									}}
								>
									<Empty
										image={Empty.PRESENTED_IMAGE_SIMPLE}
										description='No assigned credentials yet. Add credentials using the dropdown above.'
									/>
								</Flex>
							) : (
								<Space wrap size={paddingSM}>
									{groupedPersonCredentials
										.filter(
											(credential) =>
												!removedAssociatedCredentials.includes(credential.credentialID)
										)
										.map((credential) => {
											const isPasscode = credential.credentialTypeID === CREDENTIAL_TYPE_PASSCODE;
											const isLoading = !!loadingCredentialIDs[credential.credentialID];
											return (
												<Spin key={`existing-${credential.credentialID}`} spinning={isLoading} size='small'>
													<Tag
														color='magenta'
														style={{
															fontWeight: fontWeightStrong,
															borderColor: 'currentColor',
															marginInlineEnd: 0,
															opacity: isLoading ? 0.5 : 1
														}}
													>
														<Flex align='center' gap={paddingXXS}>
															{isPasscode ? (
																<NumberOutlined />
															) : (
																<CreditCardIcon color='currentColor' />
															)}
															{credential.name} - {credential.value}
															{!isLoading && (
																<CloseOutlined
																	style={{ cursor: 'pointer', color: colorIcon }}
																	onClick={() =>
																		handleRemoveCredential(credential.credentialID)
																	}
																/>
															)}
														</Flex>
													</Tag>
												</Spin>
											);
										})}

									{newAssociatedCredentials.map((credentialID) => {
										const credential = allCredentials.find((c) => c.credentialID === credentialID);
										const isPasscode = credential?.credentialTypeID === CREDENTIAL_TYPE_PASSCODE;

										return (
											<Tag
												key={`new-${credentialID}`}
												color='magenta'
												style={{
													fontWeight: fontWeightStrong,
													borderColor: 'currentColor',
													opacity: 0.7
												}}
											>
												<Flex align='center' gap={paddingXXS}>
													{isPasscode ? <NumberOutlined /> : <CreditCardOutlined />}
													{credential.name} - {credential.value} (New)
													<CloseOutlined
														style={{ cursor: 'pointer', color: colorIcon }}
														onClick={() => handleRemoveCredential(credentialID)}
													/>
												</Flex>
											</Tag>
										);
									})}
								</Space>
							)}
						</Flex>
					</Col>
				</Row>
			</Modal>
		</>
	);
};

export default ManageCredentialsModal;
